import { cloneDeep } from "lodash";
import { useState } from "react";

import { DandDitem } from "../../../../components/DragAndDropList/DragAndDropList";
import { MultiSelectItem } from "../../../../components/MultiSelect/MultiSelect";
import { getClientTransactionMapping } from "../../../../services/client.service";
import { getFund } from "../../../../services/fund.service";
import { getWorkbookById, getWorkbooksByFund } from "../../../../services/workbook.service";
import { useEffectAsync } from "../../../../utils/hooks/useEffectAsync.hook";
import { ClientTransMapping, ClientTransTypeColumn, ClientTransTypeRow } from "../../../../utils/types/clientTransMapping.type";
import { ListItem } from "../../../../utils/types/listItems";
import { AllocationColumn, AllocationColumnTypes, TransactionColumn, Workbook } from "../../../../utils/types/workbook.type";
import { applyMetricSigns } from "../allocationshared/shared";

type AllocationMapItem = {
  id: string,
  rolloverLabel: string,
  allocationId?: string
};

type CustomField = {
  label: string,
  error: boolean
};

type DAndDItemsData = {
  isUserCol: boolean,
  useArkTransactionValues: boolean
}

export const dynamicDateStr = '<dynamic date>';
export const defaultError4Message = 'This field is required';
export const workbookFrequency: ListItem[] = [{ id: "BY_YEAR", label: "Year" }, { id: "BY_QUARTER", label: "Quarter" }];
export const startQuarters: ListItem[] = [{ id: "1", label: "1" }, { id: "2", label: "2" }, { id: "3", label: "3" }, { id: "4", label: "4" }];

export const useClientTransactionMappingEffect = 
  (workbook: Workbook|undefined, clientId: string, fundId: string, importWorkbooks: Workbook[]|undefined, userName: string, onOk: (workbook: Workbook, isNewWorkbook: boolean) => void) => {

  const isNewWorkbook = workbook===undefined;

  const [fundName, setFundName] = useState<string>('');

  const [isLoading, setIsLoading] = useState(false);
  const [clientTransMapping, setClientTransMapping] = useState<ClientTransMapping>();
  const [clientTransTypeItems, setClientTransTypeItems] = useState<ClientTransTypeRow[]>([]);
  const [clientTransTypeColumns, setClientTransTypeColumns] = useState<ClientTransTypeColumn[]>([]);
  const [dandDItems, setClientTransMappingDandDItems] = useState<DandDitem[]>([]);
  const [commitmentTransTypeItems, setCommitmentTransTypeItems] = useState<MultiSelectItem[]>([]);
  const [commitmentTransTypeError, setCommitmentTransTypeError] = useState<boolean>(false);
  const [allocationMappings, setAllocationMappings] = useState<AllocationMapItem[]>([]);
  const [reportName, setReportName] = useState<string>('');
  const [reportNameError, setReportNameError] = useState<boolean>(false);
  const [frequency, setFrequency] = useState<string>('');
  const [frequencyError, setFrequencyError] = useState<boolean>(false);
  const [startQuarter, setStartQuarter] = useState<string>('1');
  const [startYear, setStartYear] = useState<string>('');
  const [decimalRounding, setDecimalRounding] = useState(-1);
  const [includeYTDColumn, setIncludeYTDColumn] = useState<boolean>(true);
  const [includeITDColumn, setIncludeITDColumn] = useState<boolean>(true);
  const [startYears, setStartYears] = useState<ListItem[]>([]);
  const [importSelections, setImportSelections] = useState<ListItem[]>([]);
  const [useCommitmentPercent, setUseCommitmentPercent] = useState<boolean>(true);
  const [commitmentPercent, setCommitmentPercent] = useState<string>(`Commitment % at ${dynamicDateStr}`);
  const [useLpPercent, setUseLpPercent] = useState<boolean>(true);
  const [lpPercent, setLpPercent] = useState<string>(`LP % at ${dynamicDateStr}`);
  const [useGpPercent, setUseGpPercent] = useState<boolean>(true);
  const [gpPercent, setGpPercent] = useState<string>(`GP % at ${dynamicDateStr}`);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);

  useEffectAsync(async (isCanceled) => {
    setIsLoading(true);

    const clientTransMappingResponse = await getClientTransactionMapping(clientId);
    const transTypes = clientTransMappingResponse.rows.filter(r => !r.useMetric);
    const clientTransTypeColumns = clientTransMappingResponse.columns;

    if (isCanceled()) return;

    setClientTransMapping(clientTransMappingResponse);

    setClientTransTypeColumns(clientTransTypeColumns);

    const year = new Date().getFullYear();
    const yearStr = year.toString();
    const prevYearStr = (year-1).toString();
    const nextYearStr = (year+1).toString();

    setStartYear(yearStr);
    setStartYears( [{ id: nextYearStr, label: nextYearStr }, { id: yearStr, label: yearStr }, { id: prevYearStr, label: prevYearStr }] );

    if(isNewWorkbook) {
      const fund = await getFund(fundId);

      if (isCanceled()) return;

      loadNewWorkbookInfo(fund.fundDetail.name, transTypes, year);
    } else {
      loadExistingWorkbookInfo(transTypes);
    }

    setIsLoading(false);
  }, []);

  const loadNewWorkbookInfo = (fundName: string, transTypes: ClientTransTypeRow[], startYear: number) => {
    const wb = workbook as Workbook;

    setFundName(fundName);

    const dAndDmappings = transTypes.map<DandDitem>((row) => {
        const DAndDItemsData = { isUserCol: false, useArkTransactionValues: false } as DAndDItemsData;

        return {
            id: row.id as string,
            name: row.type,
            data: DAndDItemsData
        };
    });
    const transTypeItems = transTypes.map<MultiSelectItem>((row) => {
      return {
          id: row.id as string,
          name: row.type,
          selected: false
      };
    });
    const allocMappings = transTypes.filter(row => row.id).map<AllocationMapItem>((row) => {
      return {
        id: row.id as string,
        rolloverLabel: row.type,
        allocationId: undefined
      };
    });

    setClientTransTypeItems(transTypes);
    setClientTransMappingDandDItems(dAndDmappings);
    setCommitmentTransTypeItems(transTypeItems);
    setAllocationMappings(allocMappings);

    
    const importSelections = !importWorkbooks ? [] : importWorkbooks.map((wb) => {
      return {
        id: wb.id ?? '',
        label: wb.reportName
      };
    });

    setImportSelections(importSelections);
  };
    
  const loadExistingWorkbookInfo = (transTypes: ClientTransTypeRow[]) => {
    const wb = workbook as Workbook;

    setClientTransTypeItems(transTypes);
    setFundName(wb.fund.name);
    setReportName(wb.reportName);
    setFrequency(wb.frequency);
    setStartYear(wb.startQuarterYear.toString());
    setStartQuarter(wb.startQuarterNumber.toString());
    setIncludeITDColumn(wb.includeItd);
    setIncludeYTDColumn(wb.includeYtd);
    
    const transTypeSelect = transTypes.map((tt) => {
      const transType = wb.commitmentTransactionTypeIds.find((type) => type === tt.type);

      return {
        id: tt.id,
        name: tt.type,
        selected: transType !== undefined
      } as MultiSelectItem;
    });

    setCommitmentTransTypeItems(transTypeSelect);

    const commitmentPercent = wb.allocationColumns.find((allocCol) => allocCol.code === 'COMMITMENT_PERCENT');
    const lpPercent = wb.allocationColumns.find((allocCol) => allocCol.code === 'LP');
    const gpPercent = wb.allocationColumns.find(allocCol => allocCol.code === 'GP');

    if(commitmentPercent) {
      setUseCommitmentPercent(true);
      setCommitmentPercent(commitmentPercent.label);
    }
    
    if(lpPercent) {
      setUseLpPercent(true);
      setLpPercent(lpPercent.label);
    }

    if(gpPercent) {
      setUseGpPercent(true);
      setGpPercent(gpPercent.label);
    }

    const customAllocCols = wb.allocationColumns.filter((allocCol) => allocCol.transactionType === AllocationColumnTypes.CUSTOM_FIELD);
    const customCols = customAllocCols.map(custCol => {
      return {
        label: custCol.label,
        error: false
      };
    });

    setCustomFields(customCols);

    const lastQuarter = wb.transactionColumns.sort((a, b) => b.quarterNumber - a.quarterNumber)[0].quarterNumber;
    const dAndDItems = wb.transactionColumns.filter(tc => tc.quarterNumber === lastQuarter).map(transType => {
      const dAndDItemsData: DAndDItemsData = {
        isUserCol: transType.isUserColumn,
        useArkTransactionValues: transType.useArkTransactionValues
      };

      return {
        id: transType.transTypeId,
        name: transType.label,
        data: dAndDItemsData,
      } as DandDitem;
    });
    
    const allocMappings = transTypes
        .sort((a,b) => (a.type > b.type) ? 1 : ((b.type > a.type) ? -1 : 0) )
        .map<AllocationMapItem>(transType => {
      const transactionColumn = wb.transactionColumns.find((col) => col.label === transType.type);

      return {
        id: transType.id as string,
        rolloverLabel: transType.type,
        allocationId: transactionColumn?.allocationCode
      };
    });

    setAllocationMappings(allocMappings);

    const selectedCommitmentTypes = transTypeSelect.filter((item) => item.selected);

    commitmentTransTypeChangeAdjustment(transTypeSelect, selectedCommitmentTypes, transTypes, dAndDItems, allocMappings);
  };


  const handleSave = async () => {    
    const oldReportName = workbook?.reportName;
    const workbooksResponse = await getWorkbooksByFund(fundId);
    const nameList = workbooksResponse.map(wb => wb.reportName);
    const existingReportName =  nameList.filter(rn => oldReportName !== rn).find(rn => rn === reportName);

    if(reportName.trim() === ''|| existingReportName) {
      setReportNameError(true);
      return;
    }

    if(frequency.trim() ==='') {
      setFrequencyError(true);
      return;
    }

    const selectedCommitmentTypes = commitmentTransTypeItems.filter((item) => item.selected);
    
    if(selectedCommitmentTypes.length === 0) {
      setCommitmentTransTypeError(true);
      return;
    }

    customFields.forEach((col) => {
      col.error = col.label.trim() === '';
    }); 

    if(customFields.find((col) => col.error)) {
      setCustomFields(cloneDeep(customFields));
      return;
    }

    const transactionCol: TransactionColumn[] = dandDItems.map((item, index) => {
      const allocMap = allocationMappings.find(allocMap => allocMap.id === item.id);

      return {
        transTypeId: item.id,
        index: index,
        label: item.name,
        locked: false,
        transSubmitError: false,
        quarterNumber: parseInt(startQuarter),
        isUserColumn: false,
        allocationCode: allocMap?.allocationId,
        metricSign: Number.MIN_VALUE, //  will be set in applyMetricSigns() function below
        useArkTransactionValues: item.data.useArkTransactionValues
      };
    });

    applyMetricSigns(transactionCol, clientTransMapping!);

    const allocationColumns: AllocationColumn[] = getAllocationColumns();
    const commitmentTransactionTypes = commitmentTransTypeItems.filter((item) => item.selected).map((item)=>item.name);

    const newWorkbook: Workbook = {
      tenantId: clientId,
      fund: {
        id: fundId,
        name: fundName
      },
      type: "ALLOCATIONS",
      reportName: reportName,
      fileName: "allocation-workbook.xlsx",
      fileType: undefined,
      frequency: frequency,
      startQuarterYear: parseInt(startYear),
      startQuarterNumber: parseInt(startQuarter),
      decimalRounding: decimalRounding,
      includeItd: includeITDColumn,
      includeYtd: includeYTDColumn,
      allocationColumns: allocationColumns,
      transactionColumns: transactionCol,
      commitmentTransactionTypeIds: commitmentTransactionTypes,
      lastRow: 0,
      lastCol: 'A'
    };

    onOk(newWorkbook, isNewWorkbook);
  };

  
  const handleUseCommitmentPercentChange = (evt: any) => {
    const wb = workbook as Workbook;

    if(!isNewWorkbook) {
      const commitmentPercent = wb.allocationColumns.find((allocCol) => allocCol.code === 'COMMITMENT_PERCENT');

      if(commitmentPercent) {
        setUseCommitmentPercent(true);
      } else {
        setUseCommitmentPercent(Boolean(evt.target.checked)); 
      }
    } else {
      setUseCommitmentPercent(Boolean(evt.target.checked)); 
    }
  };

  const handleUseLpPercentChange = (evt: any) => {
    const wb = workbook as Workbook;

    if(!isNewWorkbook) {
      const lpPercent = wb.allocationColumns.find((allocCol) => allocCol.code === 'LP');

      if(lpPercent) {
        setUseLpPercent(true);
      } else {
        setUseLpPercent(Boolean(evt.target.checked)); 
      }
    } else {
      setUseLpPercent(Boolean(evt.target.checked)); 
    }
  };

  const handleUseGpPercentChange = (evt: any) => {
    const wb = workbook as Workbook;

    if(!isNewWorkbook) {
      const gpPercent = wb.allocationColumns.find((allocCol) => allocCol.code === 'GP');

      if(gpPercent) {
        setUseGpPercent(true);
      } else {
        setUseGpPercent(Boolean(evt.target.checked)); 
      }
    } else {
      setUseGpPercent(Boolean(evt.target.checked)); 
    }
  };

  const handleOnDandDChange = (items: DandDitem[]) => {
    setClientTransMappingDandDItems(items);
  };

  const handleOnUseArkTransValueChange = (itemId: string) => {
    const updateItem = dandDItems.find(x => x.id === itemId)!;

    updateItem.data.useArkTransactionValues = !updateItem.data.useArkTransactionValues;
    setClientTransMappingDandDItems(cloneDeep(dandDItems));


    const selectedCommitmentTypes = commitmentTransTypeItems.filter(item => item.selected);

    commitmentTransTypeChangeAdjustment(commitmentTransTypeItems, selectedCommitmentTypes, clientTransTypeItems, dandDItems, allocationMappings);
  };

  const handleAddCustomColumn = () => {
    customFields.push({ label: '', error: false });
    setCustomFields(cloneDeep(customFields));
  };

  const handleEditCustomColumn = (imdex: number, evt: any) => {
    const value = evt.target.value as string;

    customFields[imdex] = { label: value, error: value.trim() === '' };
    setCustomFields(cloneDeep(customFields));
  };

  const handleRemoveCustomColumn = (position: number) => {
    customFields.splice(position, 1);
    setCustomFields(cloneDeep(customFields));
  };

  const handleWorkBookImport = async (value: string) => {
    const wb = await getWorkbookById(value);

    const items: DandDitem[] = [];

    wb.transactionColumns.forEach((col, idx) => {
      items.push({
        id: col.index?.toString() as string,
        name: col.label
      });
    });

    setClientTransMappingDandDItems(items);

  };


  function getAllocationColumns() {
    const allocationColumns: AllocationColumn[] = [];
    
    if (useCommitmentPercent) {
      addAllocationColumn('COMMITMENT_PERCENT', commitmentPercent, AllocationColumnTypes.INVESTMENT_TYPE);
    }

    if (useLpPercent) {
      addAllocationColumn('LP', lpPercent, AllocationColumnTypes.INVESTMENT_TYPE);
    }

    if (useGpPercent) {
      addAllocationColumn('GP', gpPercent, AllocationColumnTypes.INVESTMENT_TYPE);
    }

    customFields.forEach((col, idx) => {
      addAllocationColumn(`CUSTOM_FIELD_${idx}`, col.label, AllocationColumnTypes.CUSTOM_FIELD);
    });

    return allocationColumns;

    function addAllocationColumn(code: string, label: string, colType: AllocationColumnTypes) {
      const col: AllocationColumn = {
        id: undefined,
        code: code,
        transactionType: colType,
        index: allocationColumns.length + 1,
        label: label
      };

      allocationColumns.push(col);
    }
  }

  const handleAllocarionMappingChange = (rolloverID: string, allocationId: string) => {
    const mapping = allocationMappings.find((mapItem) => mapItem.id === rolloverID);

    if(mapping) {
      mapping.allocationId = allocationId;
    }

    setAllocationMappings(cloneDeep(allocationMappings));
  };

  const handleReportNameChange = (evt: any) => {
    const value = evt.target.value as string;

    setReportName(value);
    setReportNameError(value.trim() === '');
  };

  const handleFrequencyChange = (evt: any) => {
    const value = evt.target.value as string;

    setFrequency(value);
    setFrequencyError(value.trim() === '');

    if(value === 'BY_YEAR') {
      setIncludeYTDColumn( Boolean(false) );
    }
  };

  const handleCommitmentTransTypeChange = (items: MultiSelectItem[]) => {
    const selectedCommitmentTypes = commitmentTransTypeItems.filter((item) => item.selected);

    commitmentTransTypeChangeAdjustment(items, selectedCommitmentTypes, clientTransTypeItems, dandDItems, allocationMappings);
  };

  const commitmentTransTypeChangeAdjustment = 
    (items: MultiSelectItem[], selectedCommitmentTypes: MultiSelectItem[], clientTransTypeItems: ClientTransTypeRow[], dAndDItems: DandDitem[], allocationMappings: AllocationMapItem[]) => {
    
    setCommitmentTransTypeError(selectedCommitmentTypes.length === 0);
    setCommitmentTransTypeItems(items);

    // remove trans types that were selected in the Commitment Transaction Type dropdown
    let filteredTransMappingItems = clientTransTypeItems.filter(mapItem => !selectedCommitmentTypes.find((typeItem) => mapItem.id === typeItem.id));

    const tempTransMappingItems = dAndDItems.filter(item => (filteredTransMappingItems.find((mappingItem) => item.id === mappingItem.id) !== undefined) || item.data.isUserCol);

    const transMappingsToAdd = filteredTransMappingItems.filter(item => !tempTransMappingItems.find((mapItem) => mapItem.id === item.id));
    
    transMappingsToAdd.forEach(item => {
      tempTransMappingItems.push({
        id: item.id as string,
        name: item.type,
        data: { useArkTransactionValues: false }
      } as DandDitem);
    });

    setClientTransMappingDandDItems(cloneDeep(tempTransMappingItems));

    filteredTransMappingItems = filteredTransMappingItems
      .filter(x => dAndDItems.find(ddItem => ddItem.id === x.id && ddItem.data.useArkTransactionValues ) === undefined );

    const tempAllocMappingItems = filteredTransMappingItems.map(transItem => {
      const allocMappingItem = allocationMappings.find(item => item.id === transItem.id);

      if(allocMappingItem) {
        return allocMappingItem;
      } else {
        return {
          id: transItem.id,
          rolloverLabel: transItem.type,
          allocationId: undefined
        } as AllocationMapItem;
      }
    });

    setAllocationMappings(cloneDeep(tempAllocMappingItems));
  };


  const handleIncludeYTDColumnChange = (evt: any) => {
    if(isNewWorkbook) {
      setIncludeYTDColumn(Boolean(evt.target.checked)); 
    }
  };

  const handleIncludeITDColumnChange = (evt: any) => {
    if(isNewWorkbook) {
      setIncludeITDColumn(Boolean(evt.target.checked)); 
    }
  };

  return { 
    isNewWorkbook,
    isLoading,
    startYears,
    reportName, reportNameError,
    frequency, frequencyError,
    startYear, setStartYear,
    startQuarter, setStartQuarter,
    dandDItems,
    commitmentTransTypeItems, commitmentTransTypeError,
    allocationMappings,
    decimalRounding, setDecimalRounding,
    includeYTDColumn, includeITDColumn,
    importSelections,
    useCommitmentPercent, commitmentPercent, setCommitmentPercent, handleUseCommitmentPercentChange,
    useLpPercent, lpPercent, setLpPercent, handleUseLpPercentChange,
    useGpPercent, gpPercent, setGpPercent, handleUseGpPercentChange,
    customFields,
    handleOnDandDChange,
    handleOnUseArkTransValueChange,
    handleAddCustomColumn, handleEditCustomColumn, handleRemoveCustomColumn,
    handleWorkBookImport,
    getAllocationColumns,
    handleAllocarionMappingChange,
    handleSave,
    handleReportNameChange, handleFrequencyChange, handleCommitmentTransTypeChange,
    handleIncludeYTDColumnChange, handleIncludeITDColumnChange
  };
};